// TES-1201 (TES-1173 point 22, TES-1180 point 3) — the admin side of
// "Läkarens besked till patienten · visas under Din vård": which prefill a
// surface shows, the text that will be published, and why publishing is
// held. The prefill and the claim rule themselves are the contract's
// (doctor-notice.ts), so the admin and the API cannot disagree.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import {
  buildDoctorNoticePrefill,
  DOCTOR_NOTICE_CLAIM_ERROR,
  doctorNoticeUnconfirmedClaims,
  type CompletionItem,
  type CompletionOutcome,
  type DoctorNoticeConfirmations,
  type TreatmentDecisionOutcomeChoice,
} from "@care-by-ella/contracts";

interface PickedCompletion {
  items: readonly CompletionItem[];
  otherHeading?: string;
}

/** The care meeting's prefill, from its completion picker. */
export function meetingNoticePrefill(
  completion: PickedCompletion & { outcome: CompletionOutcome | null },
  confirmed: DoctorNoticeConfirmations,
): string {
  return buildDoctorNoticePrefill({
    context: "meeting",
    decision: completion.outcome,
    items: completion.outcome === "completion" ? completion.items : [],
    otherHeading: completion.otherHeading,
    confirmed,
  });
}

/** The treatment decision's prefill (the final step after a completion). */
export function decisionNoticePrefill(
  outcome: TreatmentDecisionOutcomeChoice | null,
  completion: PickedCompletion,
  confirmed: DoctorNoticeConfirmations,
): string {
  return buildDoctorNoticePrefill({
    context: "decision",
    decision: outcome,
    items: outcome === "completion" ? completion.items : [],
    otherHeading: completion.otherHeading,
    confirmed,
  });
}

/**
 * The notice as it stands. `edited` is null until the doctor types in the
 * field: until then the text follows the prefill, so ticking the remiss
 * confirmation brings its line in. Once he has written in it, his text is
 * kept as written. `startEmpty` — a meeting already documented sends no new
 * notice unless he asks for one.
 */
export function doctorNoticeText(
  edited: string | null,
  prefill: string,
  startEmpty = false,
): string {
  return edited ?? (startEmpty ? "" : prefill);
}

/** Why the notice may not be published yet, in the doctor's words. */
export function doctorNoticeClaimErrors(
  text: string,
  confirmed: DoctorNoticeConfirmations,
): string[] {
  return doctorNoticeUnconfirmedClaims(text, confirmed).map(
    (claim) => DOCTOR_NOTICE_CLAIM_ERROR[claim],
  );
}

// TES-1400 (Ö-529) — what keeps "Kan inte gå vidare" from being published
// without the doctor's own words (doctorNoticePersonalText). Ours, from
// Emelie's "personlig förklaring och rekommenderad väg framåt".
export const CANNOT_PROCEED_EXPLANATION_PROBLEM =
  "Skriv din personliga förklaring och rekommenderad väg framåt i beskedet.";
